import { useContext, useEffect, useRef } from "react"
import { GlobalMachineContext } from "../../../study-plans/pages/StudyPlansInit/StudyPlansInit"
import { ProgramsContext } from "../../../../mp-shared-components/ready-shared-components/components/Navs"
import { Program } from "../../../../../types/gqlTypes/graphql"
import { useFilterStateContext } from "../../../../reporting/providers/FiltersStateProvider"
import { StudyPlanTargets } from "../../../machines/studyPlan/types"

export const useProgramSync = () => {
  const { currentProgram, programsAndCourses } = useContext(ProgramsContext)
  const { state: filtersState, onSetFilter } = useFilterStateContext() ?? {}
  const { isOldNav, isReadOnly, currentTarget } =
    GlobalMachineContext.useSelector(state => ({
      isOldNav: state.context.isOldNav,
      isReadOnly: state.context.isReadOnly,
      currentTarget: state.context.currentTarget
    }))
  const { send } = GlobalMachineContext.useActorRef()

  // Track if we're currently auto-selecting to prevent infinite loops
  const isAutoSelectingRef = useRef(false)

  // Old Nav: Listen to ProgramsContext
  useEffect(() => {
    if (isOldNav && currentProgram) {
      send({
        type: "CHANGE_PROGRAM",
        program: currentProgram.toUpperCase() as Program
      })
    }
  }, [currentProgram, isOldNav, send])

  // New Nav: Listen to filters and auto-select first course for read-only
  useEffect(() => {
    if (!isOldNav && filtersState?.test?.[0]) {
      // Only handle single program selection (study plans don't support multiple)
      if (filtersState.test.length > 1) return

      // Don't send CHANGE_PROGRAM when in edit mode - the user is editing a plan
      if (currentTarget === StudyPlanTargets.Edit) return

      const [filterProgram, filterSubject] = filtersState.test[0].split("_")

      // Auto-select the first course when a program has no subject: read-only always,
      // editable admins (District Admin) on Scores.
      const shouldAutoSelectFirstCourse =
        isReadOnly || currentTarget === StudyPlanTargets.Scores

      if (
        shouldAutoSelectFirstCourse &&
        filterProgram &&
        !filterSubject &&
        programsAndCourses?.length > 0 &&
        onSetFilter &&
        !isAutoSelectingRef.current
      ) {
        const selectedProgramData = programsAndCourses.find(
          item => item.program === filterProgram
        )

        if (selectedProgramData?.courses?.length > 0) {
          // Set flag to prevent infinite loop
          isAutoSelectingRef.current = true

          // Auto-select first course by updating the filter
          onSetFilter("test", [
            `${filterProgram}_${selectedProgramData.courses[0].name}`
          ])

          // Reset flag after a short delay (filter update is async)
          setTimeout(() => {
            isAutoSelectingRef.current = false
          }, 100)

          // Don't send CHANGE_PROGRAM yet - let the filter update trigger it
          return
        }
      }

      // Send CHANGE_PROGRAM with the current filter values
      send({
        type: "CHANGE_PROGRAM",
        program: filterProgram,
        subject: filterSubject
      })
    }
  }, [
    filtersState?.test,
    isOldNav,
    isReadOnly,
    currentTarget,
    programsAndCourses,
    onSetFilter,
    send
  ])
}
